"use client";
import { submitOpportunityRequest } from "@/actions/submitOpportunityRequest";
import { ChangeEvent, FormEventHandler, useState } from "react";
import toast from "react-hot-toast";
import { z } from "zod";
import {
  createAdlandLandingRequestSchema,
  emptyEmailSchema,
  mobilePhoneSchema,
  requiredLongTextSchema,
  requiredShortTextSchema,
} from "@/utils/validationSchema";

const opportunityRequestType = "coopration" as const;

const opportunityFormSchema = z.object({
  firstName: requiredShortTextSchema,
  lastName: requiredShortTextSchema,
  phoneNumber: mobilePhoneSchema,
  subject: requiredShortTextSchema,
  text: requiredLongTextSchema,
});

const opportunityRequestDetailsSchema = opportunityFormSchema.omit({
  phoneNumber: true,
});

const opportunityRequestSchema = createAdlandLandingRequestSchema(
  opportunityRequestType,
  opportunityRequestDetailsSchema,
  emptyEmailSchema
);

type OpportunityFormData = z.infer<typeof opportunityFormSchema>;

const initialOpportunityFormData: OpportunityFormData = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  subject: "",
  text: "",
};

export default function JobApplicationForm() {
  const [formData, setFormData] = useState<OpportunityFormData>(
    initialOpportunityFormData
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    const name = target.name;
    const value =
      name === "phoneNumber"
        ? target.value.replace(/\D/g, "").slice(0, 11)
        : target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);

      const parsedForm = opportunityFormSchema.safeParse(formData);

      if (!parsedForm.success) {
        toast.error("اطلاعات فرم معتبر نیست.");
        return;
      }

      const validForm = parsedForm.data;
      const parsedPayload = opportunityRequestSchema.safeParse({
        requestType: opportunityRequestType,
        fullName: `${validForm.firstName} ${validForm.lastName}`.trim(),
        phone: validForm.phoneNumber,
        email: "",
        data: {
          firstName: validForm.firstName,
          lastName: validForm.lastName,
          subject: validForm.subject,
          text: validForm.text,
        },
      });

      if (!parsedPayload.success) {
        toast.error("اطلاعات فرم معتبر نیست.");
        return;
      }

      const result = await submitOpportunityRequest(parsedPayload.data);

      if (!result.success) {
        toast.error(result.message);
        return;
      }

      toast.success("با تشکر. درخواست شما برای همکاری ارسال شد");
      setFormData(initialOpportunityFormData);
    } catch {
      toast.error("ارسال درخواست با خطا مواجه شد.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      dir="rtl"
      onSubmit={handleSubmit}
      className="w-9/12 max-lg:w-11/12 max-sm:w-full mx-auto p-6 bg-white rounded-2xl"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col">
          <label htmlFor="firstName" className="mb-2 font-medium">
            نام
          </label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            placeholder="مثال: علی"
            value={formData.firstName}
            onChange={handleChange}
            className="p-3 rounded-[16px] shadow-[0_1px_4px_0_rgba(0,0,0,0.25)] focus:outline-none text-right"
            required
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="lastName" className="mb-2 font-medium">
            نام خانوادگی
          </label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            placeholder="مثال: محمدی"
            value={formData.lastName}
            onChange={handleChange}
            className="p-3 rounded-[16px] shadow-[0_1px_4px_0_rgba(0,0,0,0.25)] focus:outline-none text-right"
            required
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="phone" className="mb-2 font-medium">
            شماره تماس
          </label>
          <input
            id="phone"
            name="phoneNumber"
            type="tel"
            inputMode="numeric"
            maxLength={11}
            minLength={11}
            placeholder="0912xxxxxxx"
            value={formData.phoneNumber}
            onChange={handleChange}
            className="p-3 rounded-[16px] shadow-[0_1px_4px_0_rgba(0,0,0,0.25)] focus:outline-none text-right"
            required
          />
        </div>

        {/* <div className="flex flex-col">
          <label htmlFor="email" className="mb-2 font-medium">
            ایمیل
          </label>
          <input
            id="email"
            name="Email"
            type="email"
            placeholder="example@domain.com"
            value={formData.Email}
            onChange={handleChange}
            className="p-3 rounded-[16px] shadow-[0_1px_4px_0_rgba(0,0,0,0.25)] focus:outline-none text-right"
            required
          />
        </div> */}

        <div className="flex flex-col">
          <label htmlFor="subject" className="mb-2 font-medium">
            عنوان درخواست
          </label>
          <input
            id="subject"
            name="subject"
            type="text"
            placeholder="موضوع درخواست"
            value={formData.subject}
            onChange={handleChange}
            className="p-3 rounded-[16px] shadow-[0_1px_4px_0_rgba(0,0,0,0.25)] focus:outline-none text-right"
            required
          />
        </div>

        <div className=" flex flex-col ">
          <label htmlFor="message" className="mb-2 font-medium">
            متن پیام
          </label>
          <textarea
            id="message"
            name="text"
            rows={5}
            placeholder="متن خود را بنویسید..."
            value={formData.text}
            onChange={handleChange}
            className="p-3 rounded-[16px] shadow-[0_1px_4px_0_rgba(0,0,0,0.25)] focus:outline-none text-right"
            required
          />
        </div>
      </div>

      <div className="mt-6 text-right">
        <button
          disabled={isSubmitting}
          type="submit"
          className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          ارسال درخواست
        </button>
      </div>
    </form>
  );
}
